/* eslint-disable complexity */
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { toastError } from '@futrli/ui-components';
import {
  APIClients,
  ApiClientType,
  auth0Authenticator,
  IntegrationsAPIClient,
  Logger,
  QUERY_PARAMS,
  useLatestVersionDetection,
  validateUserEmail
} from '@futrli/shared-services';

import { resetError } from '../../core/redux-utils/actions';
import { SIGN_IN } from '../constants';
import { GET_CURRENT_AUTHENTICATED_USER } from '../../auth/constants';
import { useFederatedSignIn } from '../../federated-sign-in/use-federated-sign-in';
import { PREDICT_API_URL } from '../../../../predict/src/core/config';
import { Login } from './login';
import { SageIdLoginForm } from './sage-id-login-form';
import { clearLocalStorageWithExceptions } from '@futrli/shared-services/string-utils/utils';

export const OPTIONAL_BASE_URL = PREDICT_API_URL && { baseURL: PREDICT_API_URL };

export const SageLogin: React.FC = () => {
  const dispatch = useDispatch();

  const [isRedirecting, setIsRedirecting] = useState(false);
  const [starterLoading, setStarterLoading] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const { isFederatedSignInLoading, federatedSignInError, handleFederatedSignIn } = useFederatedSignIn();
  const isLoading = isFederatedSignInLoading || starterLoading;

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sage50Error = searchParams.get(QUERY_PARAMS.SAGE_50_ERROR);

  React.useEffect(() => {
    // Initially set it to AWS one so unauthenticated requests can be processed
    APIClients.setApiClient(ApiClientType.Aws);
    IntegrationsAPIClient.setApiClient(ApiClientType.Aws);

    clearLocalStorageWithExceptions();
    sessionStorage.clear();
    return () => {
      dispatch(resetError(SIGN_IN));
      dispatch(resetError(GET_CURRENT_AUTHENTICATED_USER));
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (sage50Error) {
      toastError(
        <FormattedMessage
          id="login.sage_50_invalid_user"
          defaultMessage="There is no business associated with Futrli. Please log in with the appropriate credentials."
        />
      );
    }
  }, [sage50Error]);

  // Browser Back may restore this page from the back-forward cache with the redirect state still active.
  React.useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsRedirecting(false);
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useLatestVersionDetection(true);

  const handleRedirect = useCallback((email: string) => {
    setIsRedirecting(true);
    APIClients.setApiClient(ApiClientType.Auth0);
    IntegrationsAPIClient.setApiClient(ApiClientType.Auth0);
    auth0Authenticator.signIn(email);
  }, []);

  const handleSubmit = useCallback(
    async (email: string) => {
      if (!email) {
        return;
      }
      try {
        setStarterLoading(true);
        setSubmittedEmail(email);

        const { redirect } = await validateUserEmail(email);
        if (!redirect) {
          setShowLoginForm(true);
        } else {
          // We don't need redirect_url from response for redirect,
          // we need to call loginWithRedirect function from auth0 lib
          handleRedirect(email);
        }
      } catch (e) {
        toastError();
        Logger.error(e as string);
      } finally {
        setStarterLoading(false);
      }
    },
    [handleRedirect]
  );

  if (showLoginForm) {
    return <Login sageIdEmail={submittedEmail} />;
  }

  return (
    <SageIdLoginForm
      isLoading={isLoading || isRedirecting}
      federatedSignInError={federatedSignInError}
      onFederatedSignIn={handleFederatedSignIn}
      onSubmit={handleSubmit}
    />
  );
};
